import { MultiStepFlowContainer } from "../../Components/MultiStepFlow"
import { useNavigate } from "react-router-dom"
import { useOutletContextPlanActions } from "../../hooks/useOutletContextPlanActions"
import Spinner from "../../Components/Spinner"
import { useOutletContextPlan } from "../../hooks/useOutletContextPlan"
import { MoreInfoVideoModal } from "../../Components/MoreInfoVideoModal"
import { CHOOSE_GUIDE_RESOURCE_URL } from "../../constants/resources"

/**
 * ChooseGuide
 *
 * Multi-step flow for choosing a guide for the mentorship plan.
 * This is step 1 of the setup checklist.
 */
const ChooseGuide = () => {
  const { handleChooseSelfAsGuide, handleChooseSomeoneElseAsGuide, error, loading } =
    useOutletContextPlanActions()
  const { guide, plan } = useOutletContextPlan()
  const navigate = useNavigate()

  const steps = [
    {
      id: "welcome",
      title: "Choose a Guide",
      encouragement: "🧭",
      encouragementClassName: "text-3xl",
      fields: [
        {
          fieldType: "radio",
          fieldName: "guide",
          label: "Who will be your student's Guide?",
          fieldProps: {
            options: [
              { value: "me", label: "It's me - let's go!" },
              { value: "someone-else", label: "Someone Else" },
            ],
          },
        },
        {
          fieldType: "content",
          content: (
            <p className="text-center text-sm text-gray-600">
              We'll extend an email invite. We encourage you to reach out to them with context as
              well.
            </p>
          ),
          showWhen: values => values.guide === "someone-else",
        },
        {
          fieldType: "text",
          fieldName: "guideFirstName",
          fieldProps: {
            placeholder: "Guide's first name, e.g. Alex",
          },
          showWhen: values => values.guide === "someone-else",
        },
        {
          fieldType: "text",
          fieldName: "guideLastName",
          fieldProps: {
            placeholder: "Guide's last name, e.g. Smith",
          },
          showWhen: values => values.guide === "someone-else",
        },
        {
          fieldType: "email",
          fieldName: "guideEmail",
          fieldProps: {
            placeholder: "Guide's email address ",
          },
          showWhen: values => values.guide === "someone-else",
        },
      ],
      renderCustomContent: () => (
        <div className="mx-auto flex max-w-xl flex-col items-center justify-center gap-3 text-center">
          <p className="text-base text-gray-700">
            Choose a trusted adult that will walk with your Student as they complete their Mentor
            Circle Plan!
          </p>
          <p className="text-base text-gray-700">
            The Guide will help your Student complete the{" "}
            <strong>
              <em>Student Activities</em>
            </strong>{" "}
            and, then, help Mentors complete the{" "}
            <strong>
              <em>Mentor Activities</em>
            </strong>
            .
          </p>
          <MoreInfoVideoModal
            src={CHOOSE_GUIDE_RESOURCE_URL}
            text=""
            postText="How to choose a Guide"
            title="Choosing a Guide"
            textClassName="text-brand no-underline"
          />
        </div>
      ),
    },
  ]

  const handleComplete = async (values, stepId) => {
    if (values.guide === "me") {
      await handleChooseSelfAsGuide().then(() => {
        navigate("../")
      })
    } else {
      // Navigate to GuideInviteSent page after inviting guide
      await handleChooseSomeoneElseAsGuide(
        values.guideFirstName,
        values.guideLastName,
        values.guideEmail,
      ).then(() => {
        navigate("../guide-invited")
      })
    }
  }

  return (
    <>
      {loading && <Spinner overlay message="Saving Guide Selection..." />}
      <MultiStepFlowContainer
        onBack={() => {
          navigate(`/plan/${plan?.token}/checklist`)
          return true
        }}
        alwaysShowBackButton={true}
        steps={steps}
        showProgress={false}
        initialValues={{ guide: "" }}
        onComplete={handleComplete}
        completeButtonText="Complete this Step"
        fieldWidth="w-64 mx-auto"
        fieldContainerClassName="flex flex-col items-center"
      />
    </>
  )
}

export default ChooseGuide
